// The hotel top bar's search (board 27, every artboard): «ابحث باسم نزيل أو
// هاتف أو رقم غرفة» → GET /hotel/reservations?view=all&q= (erp-core searches
// name, phone and room); each match = guest · room · dates and opens
// /hotel/reservations/:id. The list is the server's, in its order.

import { forwardRef, useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useDebounced } from '@/features/booking/public';
import { toArabicDigits } from '@/lib/format';
import { useReservationSearch } from '../data/hooks';
import { HOTEL_PATHS } from '../menu';
import { stayRangeLabel } from '../lib/words';
import '../screens/new-reservation.css';

export const HOTEL_SEARCH_PLACEHOLDER = 'ابحث باسم نزيل أو هاتف أو رقم غرفة';

export const HotelSearch = forwardRef<HTMLInputElement, { debounceMs?: number }>(function HotelSearch({ debounceMs = 250 }, ref) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const term = useDebounced(q.trim(), debounceMs);
  const search = useReservationSearch(term);
  const items = search.data?.items;
  const shown = open && term.length > 0 && !!items;
  // the list on screen answers what is typed NOW (not the debounced term's predecessor kept as placeholder)
  const current = term === q.trim() && term.length > 0 && !search.isPlaceholderData && !search.isFetching && !!items;
  // Enter before the answer arrives waits for it, then opens the first match of the CURRENT query (or nothing)
  const [waiting, setWaiting] = useState(false);
  useEffect(() => {
    if (!waiting || !current) return;
    setWaiting(false);
    if (items && items.length > 0) go(items[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waiting, current, items]);
  // a re-run that fails AGAIN (an error newer than the Enter) stops waiting; the next Enter tries once more
  const askedAt = useRef(0);
  useEffect(() => {
    if (waiting && search.isError && !search.isFetching && search.errorUpdatedAt >= askedAt.current) setWaiting(false);
  }, [waiting, search.isError, search.isFetching, search.errorUpdatedAt]);

  const go = (id: string) => {
    setOpen(false);
    setQ('');
    navigate(`${HOTEL_PATHS.reservations}/${encodeURIComponent(id)}`);
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!q.trim()) return;
    if (current) {
      if (items && items.length > 0) go(items[0].id);
      return;
    }
    // Enter after a FAILED search runs it again (it used to wait for an answer that was never coming)
    askedAt.current = Date.now();
    if (search.isError && !search.isFetching) void search.refetch();
    setWaiting(true);
  };

  return (
    <form className="e-search ht-search" role="search" onSubmit={submit} onBlur={(e) => !e.currentTarget.contains(e.relatedTarget as Node | null) && setOpen(false)}>
      <Search aria-hidden="true" width={18} height={18} />
      <input
        ref={ref}
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setWaiting(false);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={HOTEL_SEARCH_PLACEHOLDER}
        aria-label={HOTEL_SEARCH_PLACEHOLDER}
        role="combobox"
        aria-expanded={shown}
        aria-controls="hotel-search-results"
      />
      <kbd className="e-kbd" aria-hidden="true">
        /
      </kbd>
      {shown && (
        <ul className="ht-search-results" id="hotel-search-results" role="listbox" aria-label={HOTEL_SEARCH_PLACEHOLDER} data-testid="hotel-search-results">
          {items.length === 0 ? (
            <li className="ht-search-empty">لا نتائج لـ«{term}»</li>
          ) : (
            items.slice(0, 8).map((it) => (
              <li key={it.id} role="option" aria-selected="false" tabIndex={-1} className="ht-search-row" onMouseDown={(e) => e.preventDefault()} onClick={() => go(it.id)}>
                <span className="ht-search-name">{it.guest_name}</span>
                <span className="ht-search-sub">
                  {it.unit_label ? `غرفة ${toArabicDigits(it.unit_label)} · ` : it.unit_type_name ? `${it.unit_type_name} · ` : ''}
                  {stayRangeLabel(it.stay_from, it.stay_to, it.nights)}
                </span>
              </li>
            ))
          )}
        </ul>
      )}
    </form>
  );
});
